import { ROOM_CAPACITY } from "../../constants/editor";
import type { RoomSnapshot } from "../../shared/game";

type RoomPanelProps = {
  playerId: string;
  room: RoomSnapshot | null;
  connectionStatus: string;
  error: string | null;
  phaseRemainingMs: number | null;
  onExportCard: () => void;
  onSetReady: (isReady: boolean) => void;
  onSetRoundDuration: (durationMs: number) => void;
  onSetRoundMode: (mode: "timed" | "solo") => void;
  onRestartMatch: () => void;
  onLeaveRoom: () => void;
};

export function RoomPanel({
  playerId,
  room,
  connectionStatus,
  error,
  phaseRemainingMs,
  onExportCard,
  onSetReady,
  onSetRoundDuration,
  onSetRoundMode,
  onRestartMatch,
  onLeaveRoom,
}: RoomPanelProps) {
  const self = room?.players.find((player) => player.id === playerId) ?? null;
  const isHost = room?.hostId === playerId;

  return (
    <div className="room-topbar">
      <div className="room-topbar-meta">
        <div className="room-topbar-chip"><span>Status</span><strong>{connectionStatus}</strong></div>
        {room && <div className="room-topbar-chip"><span>Room</span><strong>{room.id}</strong></div>}
        {room && <div className="room-topbar-chip"><span>Players</span><strong>{room.players.length}/{ROOM_CAPACITY}</strong></div>}
        {room && <div className="room-topbar-chip"><span>Phase</span><strong>{room.phase}</strong></div>}
        {room && <div className="room-topbar-chip"><span>Mode</span><strong>{room.round.mode === "solo" ? "Solo" : (room.round.durationMs === 300000 ? "5 min" : "2 min")}</strong></div>}
        {phaseRemainingMs !== null && <div className="room-topbar-chip"><span>{room?.phase === "drafting" ? "Timer" : "Next"}</span><strong>{Math.max(0, Math.ceil(phaseRemainingMs / 1000))}s</strong></div>}
        {self && <div className="room-topbar-chip"><span>You</span><strong>{self.name}{isHost ? " · host" : ""}</strong></div>}
      </div>

      <div className="room-topbar-actions">
        {isHost && room?.phase === "lobby" && (
          <div style={{ display: "flex", gap: 8 }}>
            {room.players.length === 1 && (
              <button type="button" onClick={() => onSetRoundMode("solo")} className={room.round.mode === "solo" ? "active-button" : "ghost-button"}>
                Solo
              </button>
            )}
            <button type="button" onClick={() => onSetRoundDuration(120000)} className={room.round.mode === "timed" && room.round.durationMs === 120000 ? "active-button" : "ghost-button"}>
              2 Min Round
            </button>
            <button type="button" onClick={() => onSetRoundDuration(300000)} className={room.round.mode === "timed" && room.round.durationMs === 300000 ? "active-button" : "ghost-button"}>
              5 Min Round
            </button>
          </div>
        )}
        {self && room?.phase === "lobby" && room.round.mode !== "solo" && (
          <button type="button" onClick={() => onSetReady(!self.isReady)} className={self.isReady ? "active-button" : "ghost-button"}>
            {self.isReady ? "Ready" : "Mark Ready"}
          </button>
        )}
        <button type="button" onClick={onExportCard} className="ghost-button">
          Save JPG
        </button>
        {isHost && room?.phase === "lobby" && (
          <button type="button" onClick={onRestartMatch} className="active-button">
            Reset Cards
          </button>
        )}
        <button type="button" onClick={onLeaveRoom} className="ghost-button">
          Leave Room
        </button>
      </div>

      {room && (
        <div className="room-topbar-subline">
          <span>Share: {`${window.location.origin}/?room=${room.id}`}</span>
          <span>Ready: {room.players.filter((player) => player.isReady).length}/{room.players.length}</span>
          {room.phase === "lobby" && room.round.mode === "solo" && <span>Solo mode is active. You can design freely until another player joins.</span>}
          {room.phase === "lobby" && room.round.mode === "timed" && <span>When all players are ready, the timed design round begins.</span>}
          {room.phase === "drafting" && <span>Design round live. Battle begins when the timer hits zero.</span>}
          {error && <span style={{ color: "#f0a5b9" }}>{error}</span>}
        </div>
      )}
    </div>
  );
}
